import React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Collapse, Grid, IconButton, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { CSSProperties } from '@mui/material/styles';
import componentConstants from '../../../core-utils/messages/componentConstants';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  DottedText,
  ExternalLink,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import ProgressBar from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/ProgressBar';
import { numberToCurrencyFormatter } from '../../../core-utils/helper-utils/helper';
import MultiProgressBar from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/MultiProgressBar';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { getInfoIcon } from '../../../core-utils/helper-utils/infoIconHelper';

export interface DiseasePrevalenceTableProps {
  data: any;
  handleDiseaseCategory: (
     
    diseaseCategory: string,
     
    memberList: string[],
     
    isDiseaseGroup: boolean,
     
    totalCost?: {
      totalCost: number;
      rxCost: number;
      medicalCost: number;
    },
  ) => any;
  handleEpisodeCategory: (
     
    episodeCategory: string,
  ) => any;
  highestPrevalenceRate: number;
  isEpisodesOfCare?: boolean;
  isEpisodeOfCareSelected?: boolean;
  isChronicPrevalence?: boolean;
}

let maximumPrevalenceRate = 0;

const getProgressBar = (value: number, type: 'single' | 'multi') => {
  const isSingleType = type === 'single';
  const percentageSymbol = isSingleType ? '%' : '';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        color: isSingleType
          ? patientAccessTheme.palette.text.highemphasis
          : patientAccessTheme.palette.text.mediumemphasis,
      }}
    >
      <div style={{ marginRight: '8px' }}>
        {(isSingleType ? value.toFixed(2) : value) + percentageSymbol}
      </div>
      <ProgressBar
        value={isSingleType ? (value / maximumPrevalenceRate) * 100 : value}
        height={'6px'}
        width={'124px'}
        borderRadius={'4px'}
        background={
          isSingleType
            ? patientAccessTheme.palette.grey[400]
            : patientAccessTheme.palette.riskStratificationCard?.row1Color
        }
        barColor={
          isSingleType
            ? patientAccessTheme.palette.primary[300]
            : patientAccessTheme.palette.grey.A700
        }
      />
      {isSingleType ? (
        ''
      ) : (
        <div style={{ marginLeft: '8px' }}>
          {100 - value + percentageSymbol}
        </div>
      )}
    </div>
  );
};

// const progressBarForBothZero = (type: 'single' | 'multi') => {
//   const isSingleType = type === 'single';
//   const percentageSymbol = isSingleType ? '%' : '';
//   return (
//     <div
//       style={{
//         display: 'flex',
//         alignItems: 'center',
//         color: isSingleType
//           ? patientAccessTheme.palette.text.highemphasis
//           : patientAccessTheme.palette.text.mediumemphasis,
//       }}
//     >
//       <div style={{ marginRight: '8px' }}>{0 + percentageSymbol}</div>
//       <ProgressBar
//         value={100}
//         height={'6px'}
//         width={'124px'}
//         borderRadius={'4px'}
//         background={patientAccessTheme.palette.grey[400]}
//         barColor={patientAccessTheme.palette.grey[400]}
//       />
//       {isSingleType ? '' : <div style={{ marginLeft: '8px' }}>{0}</div>}
//     </div>
//   );
// };

const getMultiProgressBar = (value1: any, value2: any, value3: any) => {
  const totalValue = value1 + value2 + value3;
  const value1Percent = (value1 / totalValue) * 100;
  const value2Percent = (value2 / totalValue) * 100;
  const value3Percent = (value3 / totalValue) * 100;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: '50px',
      }}
    >
      <MultiProgressBar
        height={'1.8rem'}
        width={'350px'}
        borderRadius={'0.25rem'}
        backgroundColor={patientAccessTheme.palette.grey[100]}
        colorAndCompleted={[
          {
            completed: value1Percent,
            color: patientAccessTheme.palette.warning[500],
          },
          {
            completed: value2Percent,
            color: '#919ED0',
          },
          {
            completed: value3Percent,
            color: patientAccessTheme.palette.status.success,
          },
        ]}
      />
    </div>
  );
};

const redirectToCategories = () => {
  window.open(
    componentConstants.ORGANISMS.DISEASE_PREVALENCE_TABLE.REDIRECT_URL_DISEASE,
    '_blank',
  );
};

const redirectToCategoriesChronic = () => {
  window.open(
    componentConstants.ORGANISMS.DISEASE_PREVALENCE_TABLE.REDIRECT_URL_CHRONIC,
    '_blank',
  );
};

let tableConstants: { HEADERS: any };

const getTableHeaders = () => {
  return (
    <>
      <TableCell
        style={{
          width: '20px',
          paddingRight: '0px',
        }}
      />
      <TableCell
        align="left"
        style={{
          paddingLeft: '0px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <div>{tableConstants.HEADERS.DISEASE_CATEGORY}</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ExternalLink
              onClick={() => redirectToCategories()}
              style={{
                width: '17px',
                height: '17px',
                marginLeft: '4px',
                fill: patientAccessTheme.palette.status.icon,
                cursor: 'pointer',
              }}
            />
          </div>
        </div>
      </TableCell>
      {/* <TableCell align="left">
        {tableConstants.HEADERS.PREVALENCE_RATE}
      </TableCell> */}
      <TableCell
        align="right"
        style={{
          paddingRight: '48px',
        }}
      >
        {tableConstants.HEADERS.TOTAL_COST}
      </TableCell>
      {/* <TableCell align="center">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '175px',
          }}
        >
          <div>{tableConstants.HEADERS.MEDICAL}</div>
          <div>{tableConstants.HEADERS.RX}</div>
        </div>
      </TableCell> */}
      <TableCell align="right">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <div>{tableConstants.HEADERS.PMPM}</div>
          <div>{getInfoIcon(tableConstants.HEADERS.PMPM_TOOLTIP)}</div>
        </div>
      </TableCell>
      <TableCell align="right">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <div>{tableConstants.HEADERS.HIGH_COST}</div>
          <div>{getInfoIcon(tableConstants.HEADERS.HIGH_COST_TOOLTIP)}</div>
        </div>
      </TableCell>
    </>
  );
};

const getTableHeadersChronic = () => {
  return (
    <>
      <TableCell
        align="left"
        style={{
          paddingLeft: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <div>{tableConstants.HEADERS.DISEASE_CATEGORY}</div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ExternalLink
              onClick={() => redirectToCategoriesChronic()}
              style={{
                width: '17px',
                height: '17px',
                marginLeft: '4px',
                fill: patientAccessTheme.palette.status.icon,
                cursor: 'pointer',
              }}
            />
          </div>
        </div>
      </TableCell>
      <TableCell align="left">
        {tableConstants.HEADERS.PREVALENCE_RATE}
      </TableCell>
      <TableCell
        align="right"
        style={{
          paddingRight: '48px',
        }}
      >
        {tableConstants.HEADERS.TOTAL_COST}
      </TableCell>
      {/* <TableCell align="center">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '175px',
          }}
        >
          <div>{tableConstants.HEADERS.MEDICAL}</div>
          <div>{tableConstants.HEADERS.RX}</div>
        </div>
      </TableCell> */}
      <TableCell align="right">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <div>{tableConstants.HEADERS.PMPM}</div>
          <div>{getInfoIcon(tableConstants.HEADERS.PMPM_TOOLTIP)}</div>
        </div>
      </TableCell>
      <TableCell align="right">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <div>{tableConstants.HEADERS.HIGH_COST}</div>
          <div>{getInfoIcon(tableConstants.HEADERS.HIGH_COST_TOOLTIP)}</div>
        </div>
      </TableCell>
    </>
  );
};

const getEpisodesTableHeaders = () => {
  return (
    <>
      <TableCell
        align="left"
        style={{
          paddingLeft: '10px',
        }}
      >
        {tableConstants.HEADERS.EPISODE_NAME}
      </TableCell>
      <TableCell align="left">{tableConstants.HEADERS.EPISODES}</TableCell>
      <TableCell align="right">{tableConstants.HEADERS.TOTAL_COST}</TableCell>
      <TableCell align="right">
        {tableConstants.HEADERS.PERCENTILE_25}
      </TableCell>
      <TableCell align="center">{tableConstants.HEADERS.MEDIAN_COST}</TableCell>
      <TableCell align="right">
        {tableConstants.HEADERS.PERCENTILE_75}
      </TableCell>
    </>
  );
};

const getTableBodyRowChronic = (
  subRow: any,
  handleDiseaseCategory: (
     
    diseaseCategory: string,
     
    memberList: string[],
     
    isDiseaseGroup: boolean,
     
    totalCost: {
      totalCost: number;
      rxCost: number;
      medicalCost: number;
    },
  ) => any,
): React.JSX.Element => {
  const { classes } = makeStyles()({
    tableRow: {
      boxSizing: 'border-box',
      width: '1175px',
      height: '56px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`,
      '& .MuiTableCell-body': {
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.mediumemphasis,
      },
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
      borderBottom: 'none',
    },
    subRow: {
      border: 'none',
      backgroundColor: patientAccessTheme.palette.background.paper,
      '& .MuiTableCell-root': {
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.mediumemphasis,
        borderBottom: 'none',
      },
    },
    textOverflowStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
    },
  })();

  const avgHighCostStyles: CSSProperties = {
    lineHeight: '24px',
    fontWeight: 400,
    color: patientAccessTheme.palette.text.mediumemphasis,
  };

  return (
    <React.Fragment>
      <TableRow className={classes.tableRow}>
        <TableCell
          style={{
            paddingLeft: '20px',
            wordBreak: 'break-word',
          }}
          onClick={() =>
            handleDiseaseCategory(
              subRow?.chronicDiseaseName,
              subRow?.disease_member_list,
              false,
              {
                totalCost: subRow?.mainMeasure?.totalCost,
                rxCost: subRow?.mainMeasure?.rxCost,
                medicalCost: subRow?.mainMeasure?.medicalCost,
              },
            )
          }
        >
          {/* {getTruncatedText(subRow?.chronicDiseaseName ?? '','150px')} */}
          {<DottedText truncateText text={subRow?.chronicDiseaseName ?? ''} />}
        </TableCell>
        <TableCell>
          {getProgressBar(subRow?.prevalenceRate, 'single')}
        </TableCell>
        <TableCell
          style={{
            ...avgHighCostStyles,
            textAlign: 'right',
            width: '150px',
          }}
        >
          {numberToCurrencyFormatter(subRow?.totalCost)}
        </TableCell>
        {/* <TableCell style={{ width: '221px' }}>
          {subRow?.medicalPercent === 0 && subRow?.rxPercent === 0
            ? progressBarForBothZero('multi')
            : getProgressBar(subRow?.medicalPercent, 'multi')}
        </TableCell> */}
        <TableCell
          style={{
            width: '150px',
            textAlign: 'right',
          }}
        >
          {numberToCurrencyFormatter(subRow?.pmpm)}
        </TableCell>
        <TableCell
          style={{ ...avgHighCostStyles, textAlign: 'right', width: '150px' }}
        >
          {numberToCurrencyFormatter(subRow?.highCost)}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const getTableBodyRow = (
  row: any,
  handleDiseaseCategory: (
     
    diseaseCategory: string,
     
    memberList: string[],
     
    isDiseaseGroup: boolean,
     
    totalCost: {
      totalCost: number;
      rxCost: number;
      medicalCost: number;
    },
  ) => any,
): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);

  const { classes } = makeStyles()({
    tableRow: {
      boxSizing: 'border-box',
      width: '1175px',
      height: '56px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`,
      '& .MuiTableCell-body': {
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.mediumemphasis,
      },
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
      borderBottom: open
        ? `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`
        : 'none',
    },
    subRow: {
      border: 'none',
      backgroundColor: patientAccessTheme.palette.background.paper,
      '& .MuiTableCell-root': {
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.mediumemphasis,
        borderBottom: 'none',
      },
    },
    textOverflowStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
    },
  })();

  const avgHighCostStyles: CSSProperties = {
    lineHeight: '24px',
    fontWeight: 400,
    color: patientAccessTheme.palette.text.mediumemphasis,
  };

  return (
    <React.Fragment>
      {/* main row */}
      <TableRow className={classes.tableRow}>
        <TableCell style={{ paddingRight: '0px' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            style={{ padding: '0px', marginRight: '12px' }}
          >
            {open ? (
              <KeyboardArrowUpIcon
                style={{ color: 'patientAccessTheme.palette.status.icon' }}
              />
            ) : (
              <KeyboardArrowDownIcon
                style={{ color: 'patientAccessTheme.palette.status.icon' }}
              />
            )}
          </IconButton>
        </TableCell>
        <TableCell
          style={{
            paddingLeft: '0px',
            wordBreak: 'break-word',
          }}
          onClick={() =>
            handleDiseaseCategory(
              row?.chronicDiseaseGroupName,
              row?.disease_member_list,
              true,
              {
                totalCost: row?.totalCost,
                rxCost: row?.rxCost,
                medicalCost: row?.medicalCost,
              },
            )
          }
        >
          {<DottedText text={row?.chronicDiseaseGroupName ?? ''} />}
        </TableCell>
        {/* <TableCell>{getProgressBar(row?.prevalenceRate, 'single')}</TableCell> */}
        <TableCell
          style={{ textAlign: 'right', paddingRight: '48px', width: '200px' }}
        >
          {numberToCurrencyFormatter(row?.totalCost)}
        </TableCell>
        {/* <TableCell>
          {row?.medicalPercent === 0 && row?.rxPercent === 0
            ? progressBarForBothZero('multi')
            : getProgressBar(row?.medicalPercent, 'multi')}
        </TableCell> */}
        <TableCell
          style={{
            ...avgHighCostStyles,
            textAlign: 'right',
            width: '150px',
          }}
        >
          {numberToCurrencyFormatter(row?.pmpm)}
        </TableCell>
        <TableCell
          style={{ ...avgHighCostStyles, textAlign: 'right', width: '150px' }}
        >
          {numberToCurrencyFormatter(row?.highCost)}
        </TableCell>
      </TableRow>

      {/* sub row */}
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={10}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table>
              <TableBody>
                {row?.individualDiseases?.map((subRow: any, index: number) => {
                  return (
                    <TableRow className={classes.subRow} key={index}>
                      <TableCell
                        style={{
                          width: '660px',
                          paddingLeft: '48px',
                          wordBreak: 'break-word',
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          handleDiseaseCategory(
                            subRow?.chronicDiseaseName,
                            subRow?.disease_member_list,
                            false,
                            {
                              totalCost: row?.totalCost,
                              rxCost: row?.rxCost,
                              medicalCost: row?.medicalCost,
                            },
                          )
                        }
                      >
                        {<DottedText text={subRow?.chronicDiseaseName ?? ''} />}
                      </TableCell>
                      {/* <TableCell>
                        {getProgressBar(subRow?.prevalenceRate, 'single')}
                      </TableCell> */}
                      <TableCell
                        style={{
                          width: '200px',
                          textAlign: 'right',
                          paddingRight: '48px',
                        }}
                      >
                        {numberToCurrencyFormatter(subRow?.totalCost)}
                      </TableCell>
                      {/* <TableCell style={{ width: '221px' }}>
                        {subRow?.medicalPercent === 0 && subRow?.rxPercent === 0
                          ? progressBarForBothZero('multi')
                          : getProgressBar(subRow?.medicalPercent, 'multi')}
                      </TableCell> */}
                      <TableCell
                        style={{
                          width: '150px',
                          textAlign: 'right',
                        }}
                      >
                        {numberToCurrencyFormatter(subRow?.pmpm)}
                      </TableCell>
                      <TableCell style={{ width: '150px', textAlign: 'right' }}>
                        {numberToCurrencyFormatter(subRow?.highCost)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const getEpisodesSelectedTableHeaders = () => {
  return (
    <>
      <TableCell
        style={{
          width: '20px',
          paddingRight: '0px',
        }}
      />
      <TableCell
        align="left"
        style={{
          paddingLeft: '0px',
          width: '195px',
        }}
      >
        {tableConstants.HEADERS.COST_TYPE}
      </TableCell>
      <TableCell
        align="right"
        style={{
          paddingRight: '40px',
        }}
      >
        {tableConstants.HEADERS.TOTAL_COST}
      </TableCell>
      <TableCell align="right">
        {tableConstants.HEADERS.PERCENTILE_25}
      </TableCell>
      <TableCell align="center">{tableConstants.HEADERS.MEDIAN_COST}</TableCell>
      <TableCell align="right">
        {tableConstants.HEADERS.PERCENTILE_75}
      </TableCell>
    </>
  );
};

const getEpisodesTableBodyRow = (
  row: any,
  handleEpisodeCategory: (
     
    episodeCategory: string,
  ) => any,
): React.JSX.Element => {
  const { classes } = makeStyles()({
    tableRow: {
      boxSizing: 'border-box',
      width: '1175px',
      height: '56px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`,
    },
    textOverflowStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
    },
    tooltip: {
      maxWidth: 'fit-content',
      padding: '10px',
      width: 'fit-content',
      backgroundColor: patientAccessTheme.palette.grey[900],
      borderRadius: '4px',
      marginTop: '10px',
    },
    item: {
      marginBottom: '6px',
      gap: '5px',
    },
  })();

  const commonTypographyProps = {
    fontSize: '12px',
    lineHeight: '20px',
    textColor: patientAccessTheme.palette.status.white,
  };
  const constants = componentConstants.ORGANISMS.EPISODES_OF_CARE_TOOLTIP;
  const getLabelValueComponent = (label: string, value: string, color: any) => {
    return (
      <Grid
        container
        direction="row"
        className={classes.item}
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid>
          <div
            style={{
              backgroundColor: color,
              height: '8px',
              width: '8px',
              borderRadius: '2px',
            }}
          ></div>
        </Grid>
        <Grid style={{ width: '100px' }}>
          <Typography {...commonTypographyProps} margin="0px 4px 0px 0px">
            {label}
          </Typography>
        </Grid>
        <Grid>
          <Typography {...commonTypographyProps} margin="0px 4px 0px 0px">
            {'-'}
          </Typography>
        </Grid>
        <Grid style={{ width: '90px', textAlign: 'right' }}>
          <Typography {...commonTypographyProps} fontType="semiBold">
            {value}
          </Typography>
        </Grid>
      </Grid>
    );
  };
  const title = (
    <Grid
      container
      sx={{
        flexDirection: 'column',
      }}
    >
      {getLabelValueComponent(
        constants.TOOL_TIP.PRE_OPERATIVE,
        `${numberToCurrencyFormatter(row?.preOperative, true, 2)}`,
        patientAccessTheme.palette.warning[500],
      )}
      {getLabelValueComponent(
        constants.TOOL_TIP.EVENT_COST,
        `${numberToCurrencyFormatter(row?.surgeryCost, true, 2)}`,
        '#919ED0',
      )}
      {getLabelValueComponent(
        constants.TOOL_TIP.POST_OPERATIVE,
        `${numberToCurrencyFormatter(row?.postOperative, true, 2)}`,
        patientAccessTheme.palette.status.success,
      )}
    </Grid>
  );
  return (
    <React.Fragment>
      {/* main row */}
      <TableRow className={classes.tableRow}>
        <TableCell
          style={{
            paddingLeft: '10px',
            wordBreak: 'break-word',
            width: '300px',
          }}
          onClick={() =>
            handleEpisodeCategory ? handleEpisodeCategory(row?.episodeName) : ''
          }
        >
          {<DottedText text={row?.episodeName ?? ''} />}
        </TableCell>
        <TableCell>{row?.episodes ?? ''}</TableCell>
        <TableCell style={{ textAlign: 'right', width: '150px' }}>
          {numberToCurrencyFormatter(row?.totalCost)}
        </TableCell>
        <TableCell style={{ textAlign: 'right', width: '150px' }}>
          {numberToCurrencyFormatter(row?.percentile25)}
        </TableCell>

        <TableCell>
          <Tooltip title={title} classes={{ tooltip: classes.tooltip }} arrow>
            <div>
              <div
                style={{
                  display: 'flex',
                  background: patientAccessTheme.palette.grey[300],
                  height: '3px',
                  width: '450px',
                  alignItems: 'center',
                  borderRadius: '10px',
                }}
              >
                {getMultiProgressBar(
                  row?.preOperative,
                  row?.surgeryCost,
                  row?.postOperative,
                )}
                <div
                  style={{
                    marginLeft: '-250px',
                    textAlign: 'right',
                    width: '110px',
                    color: `${patientAccessTheme.palette.status.white}`,
                  }}
                >
                  {numberToCurrencyFormatter(row.medianCost)}
                </div>
              </div>
            </div>
          </Tooltip>
        </TableCell>
        <TableCell style={{ textAlign: 'right', width: '150px' }}>
          {numberToCurrencyFormatter(row?.percentile75)}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

const getEpisodesSelectedTableBodyRow = (row: any): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);

  const { classes } = makeStyles()({
    tableRow: {
      boxSizing: 'border-box',
      width: '1175px',
      height: '56px',
      background: patientAccessTheme.palette.status.white,
      border: `1px solid ${patientAccessTheme.palette.basicTable?.headerBackground}`,
    },
    subRow: {
      border: 'none',
      backgroundColor: patientAccessTheme.palette.background.paper,
      '& .MuiTableCell-root': {
        fontSize: '14px',
        lineHeight: '28px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.mediumemphasis,
        borderBottom: 'none',
      },
    },
    textOverflowStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
    },
  })();

  return (
    <React.Fragment>
      {/* main row */}
      <TableRow className={classes.tableRow}>
        <TableCell style={{ paddingRight: '0px' }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            style={{ padding: '0px', marginRight: '12px' }}
          >
            {open ? (
              <KeyboardArrowUpIcon
                style={{ color: 'patientAccessTheme.palette.status.icon' }}
              />
            ) : (
              <KeyboardArrowDownIcon
                style={{ color: 'patientAccessTheme.palette.status.icon' }}
              />
            )}
          </IconButton>
        </TableCell>
        <TableCell
          style={{
            paddingLeft: '0px',
            wordBreak: 'break-word',
            width: '150px',
          }}
        >
          {row?.costType ?? ''}
        </TableCell>
        <TableCell
          style={{ textAlign: 'right', paddingRight: '40px', width: '150px' }}
        >
          {numberToCurrencyFormatter(row?.totalCost)}
        </TableCell>
        <TableCell style={{ textAlign: 'right', width: '150px' }}>
          {numberToCurrencyFormatter(row?.percentile25)}
        </TableCell>

        <TableCell>
          <div
            style={{
              display: 'flex',
              background: patientAccessTheme.palette.grey[300],
              height: '3px',
              width: '450px',
              alignItems: 'center',
              borderRadius: '10px',
            }}
          >
            {getMultiProgressBar(0, 100, 0)}
            <div
              style={{
                marginLeft: '-250px',
                textAlign: 'right',
                width: '110px',
                color: `${patientAccessTheme.palette.status.white}`,
              }}
            >
              {numberToCurrencyFormatter(row.medianCost)}
            </div>
          </div>
        </TableCell>
        <TableCell style={{ textAlign: 'right', width: '150px' }}>
          {numberToCurrencyFormatter(row?.percentile75)}
        </TableCell>
      </TableRow>

      {/* sub row */}
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table>
              <TableBody>
                {row?.subType?.map((subRow: any, index: number) => {
                  return (
                    <TableRow key={index} className={classes.subRow}>
                      <TableCell
                        style={{
                          paddingLeft: '48px',
                          wordBreak: 'break-word',
                          width: '255px',
                        }}
                      >
                        {subRow?.costType ?? ''}
                      </TableCell>
                      <TableCell
                        style={{
                          textAlign: 'right',
                          paddingRight: '48px',
                          width: '150px',
                        }}
                      >
                        {numberToCurrencyFormatter(subRow?.totalCost)}
                      </TableCell>
                      <TableCell style={{ textAlign: 'right', width: '150px' }}>
                        {numberToCurrencyFormatter(subRow?.percentile25)}
                      </TableCell>

                      <TableCell>
                        <div
                          style={{
                            display: 'flex',
                            background: patientAccessTheme.palette.grey[300],
                            height: '3px',
                            width: '450px',
                            alignItems: 'center',
                            borderRadius: '10px',
                          }}
                        >
                          {getMultiProgressBar(0, 100, 0)}
                          <div
                            style={{
                              marginLeft: '-250px',
                              textAlign: 'right',
                              width: '110px',
                              color: `${patientAccessTheme.palette.status.white}`,
                            }}
                          >
                            {numberToCurrencyFormatter(subRow.medianCost)}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell style={{ textAlign: 'right', width: '150px' }}>
                        {numberToCurrencyFormatter(subRow?.percentile75)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
const DiseasePrevalenceTable: React.FC<DiseasePrevalenceTableProps> = ({
  data,
  handleDiseaseCategory,
  handleEpisodeCategory,
  highestPrevalenceRate,
  isEpisodesOfCare = false,
  isEpisodeOfCareSelected = false,
  isChronicPrevalence,
}) => {
  if (isEpisodesOfCare) {
    tableConstants = componentConstants.ORGANISMS.EPISODES_OF_CARE_TABLE;
  } else if (isEpisodeOfCareSelected) {
    tableConstants =
      componentConstants.ORGANISMS.EPISODES_OF_CARE_SELECTED_TABLE;
  } else {
    tableConstants = componentConstants.ORGANISMS.DISEASE_PREVALENCE_TABLE;
  }

  const { classes } = makeStyles()({
    root: {
      width: isEpisodesOfCare || isEpisodeOfCareSelected ? '1127x' : '1175px',
    },
    tableHeader: {
      backgroundColor: patientAccessTheme.palette.grey[400],
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      height: '48px',
      '& .MuiTableCell-head': {
        fontSize: '12px',
        lineHeight: '12px',
        fontWeight: 500,
        color: patientAccessTheme.palette.text.highemphasis,
      },
    },
  })();

  maximumPrevalenceRate = highestPrevalenceRate;

  return (
    <TableContainer className={classes.root}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow className={classes.tableHeader}>
            {isEpisodesOfCare
              ? getEpisodesTableHeaders()
              : isEpisodeOfCareSelected
              ? getEpisodesSelectedTableHeaders()
              : isChronicPrevalence
              ? getTableHeadersChronic()
              : getTableHeaders()}
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((row: any, index: number) => {
            return (
              <React.Fragment key={index}>
                {isEpisodesOfCare
                  ? getEpisodesTableBodyRow(row, handleEpisodeCategory)
                  : isEpisodeOfCareSelected
                  ? getEpisodesSelectedTableBodyRow(row)
                  : isChronicPrevalence
                  ? getTableBodyRowChronic(row, handleDiseaseCategory)
                  : getTableBodyRow(row, handleDiseaseCategory)}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
export default DiseasePrevalenceTable;
